import { $insertList } from '@lexical/list'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
  $createHeadingNode,
  $createQuoteNode,
  HeadingTagType,
} from '@lexical/rich-text'
import { $patchStyleText, $setBlocksType } from '@lexical/selection'
import { i18n, type MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react/macro'
import { message } from 'antd'
import { Dropdown } from 'components/form/inputs/Dropdown'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { INSERT_IMAGE_COMMAND } from 'features/richText'
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
} from 'lexical'
import { Image } from 'lucide-react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

const ToolbarContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid ${({ theme }) => theme.forecast.border2};
  background: transparent;
  border-radius: 0;
  flex-wrap: wrap;
  margin-bottom: 0.5rem;

  @media (max-width: 768px) {
    padding: 0.5rem;
    gap: 0.2rem;
  }
`

const ToolbarButton = styled.button.attrs({ type: 'button' })<{
  $active?: boolean
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.375rem 0.5rem;
  border: 1px solid
    ${({ theme, $active }) =>
      $active ? theme.forecast.primary : 'transparent'};
  background: ${({ theme, $active }) =>
    $active ? theme.forecast.primary + '20' : 'transparent'};
  color: ${({ theme }) => theme.forecast.defaultText};
  border-radius: 0.25rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  min-width: 2rem;
  height: 2rem;

  @media (hover: hover) {
    &:hover {
      background: ${({ theme }) => theme.forecast.glassBackground};
      border-color: ${({ theme }) => theme.forecast.border2};
    }
  }

  &:active {
    transform: translateY(1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const StyledDropdownWrapper = styled.div<{ $minWidth?: string }>`
  min-width: ${({ $minWidth }) => $minWidth || '8rem'};

  .rs-picker-default .rs-picker-toggle.rs-btn {
    font-size: 0.875rem;
    padding: 0.375rem 0.5rem;
    border: 1px solid ${({ theme }) => theme.forecast.border2};
    background: ${({ theme }) => theme.forecast.cardGroupBackground};
    color: ${({ theme }) => theme.forecast.defaultText};

    &:hover {
      background: ${({ theme }) => theme.forecast.glassBackground};
      border-color: ${({ theme }) => theme.forecast.border2};
    }

    &:focus {
      border-color: ${({ theme }) => theme.forecast.primary};
    }
  }

  .rs-picker-toggle-value {
    color: ${({ theme }) => theme.forecast.defaultText};
  }
`

const ToolbarDivider = styled.div`
  width: 1px;
  height: 1.5rem;
  background: ${({ theme }) => theme.forecast.border2};
  margin: 0 0.25rem;
`

const ListIconWrapper = styled.span`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  align-items: flex-start;
  justify-content: center;
  color: ${({ theme }) => theme.forecast.secondaryText};
  width: 1.5rem;
`

const OrderedListRow = styled.span`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  line-height: 1;
  width: 100%;
`

const NumberLabel = styled.span`
  width: 0.5rem;
  text-align: right;
  font-size: 0.6rem;
  font-weight: 600;
  display: flex;
  justify-content: flex-end;
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-variant-numeric: tabular-nums;
`

const ListLine = styled.span`
  display: block;
  width: 1rem;
  height: 0.11rem;
  border-radius: 999px;
  background: ${({ theme }) => theme.forecast.secondaryText};
  opacity: 0.9;
  flex: 1;
`

const BulletDot = styled.span`
  display: block;
  width: 0.22rem;
  height: 0.22rem;
  border-radius: 50%;
  background: ${({ theme }) => theme.forecast.secondaryText};
`

const OrderedListIcon: React.FC = () => (
  <ListIconWrapper>
    <OrderedListRow>
      <NumberLabel>1</NumberLabel>
      <ListLine />
    </OrderedListRow>
    <OrderedListRow>
      <NumberLabel>2</NumberLabel>
      <ListLine />
    </OrderedListRow>
  </ListIconWrapper>
)

const UnorderedListIcon: React.FC = () => (
  <ListIconWrapper>
    <OrderedListRow>
      <BulletDot />
      <ListLine />
    </OrderedListRow>
    <OrderedListRow>
      <BulletDot />
      <ListLine />
    </OrderedListRow>
  </ListIconWrapper>
)

const HiddenFileInput = styled.input`
  display: none;
`

const BLOCK_TYPE_OPTIONS: Array<{ value: string; label: MessageDescriptor }> = [
  { value: 'paragraph', label: msg`Paragraph` },
  { value: 'h1', label: msg`Heading 1` },
  { value: 'h2', label: msg`Heading 2` },
  { value: 'h3', label: msg`Heading 3` },
  { value: 'quote', label: msg`Quote` },
]

const FONT_SIZE_OPTIONS: Array<{
  value: string
  label: MessageDescriptor | string
}> = [
  { value: '', label: msg`Default` },
  { value: '10px', label: '10' },
  { value: '12px', label: '12' },
  { value: '14px', label: '14' },
  { value: '16px', label: '16' },
  { value: '18px', label: '18' },
  { value: '20px', label: '20' },
  { value: '24px', label: '24' },
  { value: '28px', label: '28' },
  { value: '32px', label: '32' },
  { value: '36px', label: '36' },
  { value: '48px', label: '48' },
]

export interface ToolbarPluginProps {
  entryIdForImages?: string
}

export const ToolbarPlugin: React.FC<ToolbarPluginProps> = ({
  entryIdForImages,
}) => {
  const { t } = useLingui()
  const [editor] = useLexicalComposerContext()
  const { rolodexUploadImage } = useRolodexEdit()
  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    underline: false,
    strikethrough: false,
  })
  const [blockType, setBlockType] = useState('paragraph')
  const [fontSize, setFontSize] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)

  const updateToolbar = useCallback(() => {
    const selection = $getSelection()
    if ($isRangeSelection(selection)) {
      setActiveFormats({
        bold: selection.hasFormat('bold'),
        italic: selection.hasFormat('italic'),
        underline: selection.hasFormat('underline'),
        strikethrough: selection.hasFormat('strikethrough'),
      })
    }
  }, [])

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        updateToolbar()
      })
    })
  }, [editor, updateToolbar])

  const formatText = (
    format: 'bold' | 'italic' | 'underline' | 'strikethrough'
  ) => {
    editor.focus()
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, format)
  }

  const formatBlock = (type: string) => {
    editor.focus()
    if (type === 'paragraph') {
      editor.update(() => {
        const selection = $getSelection()
        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createParagraphNode())
        }
      })
    } else if (type === 'quote') {
      editor.update(() => {
        const selection = $getSelection()
        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createQuoteNode())
        }
      })
    } else if (type.startsWith('h')) {
      editor.update(() => {
        const selection = $getSelection()
        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () =>
            $createHeadingNode(type as HeadingTagType)
          )
        }
      })
    }
    setBlockType(type)
  }

  const insertList = (type: 'ul' | 'ol') => {
    editor.focus()
    editor.update(() => {
      $insertList(type === 'ul' ? 'bullet' : 'number')
    })
  }

  const applyFontSize = (size: string) => {
    editor.focus()
    editor.update(() => {
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        $patchStyleText(selection, { 'font-size': size || null })
      }
    })
    setFontSize(size)
  }

  const handleImageButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const handleToolbarMouseDown = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault()
  }

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0]
    if (!file || !entryIdForImages || isUploading) {
      return
    }

    try {
      const maxSizeInMB = 10
      if (file.size > maxSizeInMB * 1024 * 1024) {
        message.error(t`File must be smaller than ${maxSizeInMB}MB`)
        return
      }

      const acceptedFileTypes = ['.png', '.jpg', '.jpeg', '.gif', '.webp']
      const nameParts = file.name.split('.')
      let fileExtension = ''
      if (nameParts.length > 1) {
        fileExtension = `.${nameParts.pop()!.toLowerCase()}`
      }
      if (!acceptedFileTypes.includes(fileExtension)) {
        const acceptedList = acceptedFileTypes.join(', ')
        message.error(t`File type not accepted. Please upload: ${acceptedList}`)
        return
      }

      setIsUploading(true)

      const timestamp = Date.now()
      const sanitizedFileName = file.name
        .replace(/[^a-zA-Z0-9.-]/g, '-')
        .toLowerCase()
      const imagePath = `images/${timestamp}-${sanitizedFileName}`

      const hideLoading = message.loading(t`Uploading image...`, 0)

      try {
        await rolodexUploadImage(entryIdForImages, file, imagePath)

        hideLoading()
        message.success(t`Image uploaded successfully`)

        editor.dispatchCommand(INSERT_IMAGE_COMMAND, {
          entryId: entryIdForImages,
          imagePath,
          altText: file.name,
        })
      } catch (error) {
        hideLoading()
        console.error('Upload failed:', error)
        message.error(t`Failed to upload image`)
      }
    } catch (error) {
      console.error('Image upload error:', error)
      message.error(t`An error occurred during upload`)
    } finally {
      setIsUploading(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const blockTypeOptions = BLOCK_TYPE_OPTIONS.map((option) => ({
    value: option.value,
    label: i18n._(option.label),
  }))
  const fontSizeOptions = FONT_SIZE_OPTIONS.map((option) => ({
    value: option.value,
    label:
      typeof option.label === 'string' ? option.label : i18n._(option.label),
  }))

  return (
    <ToolbarContainer>
      {/* Block type selector */}
      <StyledDropdownWrapper>
        <Dropdown
          options={blockTypeOptions}
          defaultValue={blockType}
          onValueChange={formatBlock}
          placeholder={t`Format`}
          cleanable={false}
          searchable={false}
          mode='edit'
          $backgroundColor='transparent'
        />
      </StyledDropdownWrapper>

      {/* Font size selector */}
      <StyledDropdownWrapper $minWidth='5rem'>
        <Dropdown
          options={fontSizeOptions}
          defaultValue={fontSize}
          onValueChange={applyFontSize}
          placeholder={t`Size`}
          cleanable={false}
          searchable={false}
          mode='edit'
          $backgroundColor='transparent'
        />
      </StyledDropdownWrapper>

      <ToolbarDivider />

      {/* Text formatting */}
      <ToolbarButton
        $active={activeFormats.bold}
        onMouseDown={handleToolbarMouseDown}
        onClick={() => formatText('bold')}
        title={t`Bold (Ctrl+B)`}>
        <strong>B</strong>
      </ToolbarButton>

      <ToolbarButton
        $active={activeFormats.italic}
        onMouseDown={handleToolbarMouseDown}
        onClick={() => formatText('italic')}
        title={t`Italic (Ctrl+I)`}>
        <em>I</em>
      </ToolbarButton>

      <ToolbarButton
        $active={activeFormats.underline}
        onMouseDown={handleToolbarMouseDown}
        onClick={() => formatText('underline')}
        title={t`Underline (Ctrl+U)`}>
        <u>U</u>
      </ToolbarButton>

      <ToolbarButton
        $active={activeFormats.strikethrough}
        onMouseDown={handleToolbarMouseDown}
        onClick={() => formatText('strikethrough')}
        title={t`Strikethrough`}>
        <s>S</s>
      </ToolbarButton>

      {/* Lists */}
      <ToolbarButton
        onMouseDown={handleToolbarMouseDown}
        onClick={() => insertList('ol')}
        title={t`Numbered List`}>
        <OrderedListIcon />
      </ToolbarButton>

      <ToolbarButton
        onMouseDown={handleToolbarMouseDown}
        onClick={() => insertList('ul')}
        title={t`Bullet List`}>
        <UnorderedListIcon />
      </ToolbarButton>

      {/* Image Upload */}
      {entryIdForImages && (
        <>
          <ToolbarDivider />
          <ToolbarButton
            onMouseDown={handleToolbarMouseDown}
            onClick={handleImageButtonClick}
            title={isUploading ? t`Uploading...` : t`Upload Image`}
            disabled={isUploading}>
            <Image size={20} />
          </ToolbarButton>
          <HiddenFileInput
            ref={fileInputRef}
            type='file'
            accept='.png,.jpg,.jpeg,.gif,.webp'
            onChange={handleFileChange}
            disabled={isUploading}
          />
        </>
      )}
    </ToolbarContainer>
  )
}
